import { Circle } from '@mui/icons-material';
import { Chip as MuiChip } from '@mui/material';
import { makeStyles } from '@mui/styles';

type Props = {
  label: string;
  color?: string;
  colorStyles?: { [key: string]: { [key: string]: any } };
  colorIcons?: { [key: string]: JSX.Element | undefined };
  statusStyle?: { [key: string]: any };
  size?: ChipSize;
  showIcon?: boolean;
  onClick?: Function;
  muiChipProps?: any;
};

export enum ChipDefaultColor {
  BLACK = 'BLACK',
  BLACK_BACKGROUND = 'BLACK_BACKGROUND',
  BLUE = 'BLUE',
  GRAY = 'GRAY',
  GREEN = 'GREEN',
  LIGHT_BLUE = 'LIGHT_BLUE',
  LIGHT_GREEN = 'LIGHT_GREEN',
  LIGHT_RED = 'LIGHT_RED',
  ORANGE = 'ORANGE',
  PURPLE = 'PURPLE',
  RED = 'RED',
  YELLOW = 'YELLOW',
}

export enum ChipSize {
  MEDIUM = 'medium',
  SMALL = 'small',
}

export const ChipDefaultColorStyle: { [key: string]: { [key: string]: any } } = {
  [ChipDefaultColor.BLACK]: {
    color: '#1C2541',
    backgroundColor: '#F9F9FB',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '13px',
  },
  [ChipDefaultColor.BLACK_BACKGROUND]: {
    color: '#FFFFFF',
    backgroundColor: '#000000',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '13px',
  },
  [ChipDefaultColor.BLUE]: {
    color: '#2E90FA',
    backgroundColor: '#EFF8FF',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '13px',
  },
  [ChipDefaultColor.GRAY]: {
    color: '#1C2541',
    backgroundColor: '#D9D9DB',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '13px',
  },
  [ChipDefaultColor.GREEN]: {
    color: '#027A48',
    backgroundColor: '#ECFDF7',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '13px',
  },
  [ChipDefaultColor.LIGHT_BLUE]: {
    color: '#5ED1F0',
    backgroundColor: '#EFF8FF',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '13px',
  },
  [ChipDefaultColor.LIGHT_GREEN]: {
    color: '#1A7B53',
    backgroundColor: '#ECFDF7',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '13px',
  },
  [ChipDefaultColor.LIGHT_RED]: {
    color: '#DB5461',
    backgroundColor: '#FEF3F2',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '13px',
  },
  [ChipDefaultColor.ORANGE]: {
    color: '#F79009',
    backgroundColor: '#FEF3F2',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '13px',
  },
  [ChipDefaultColor.PURPLE]: {
    color: '#772AD8',
    backgroundColor: '#F4F3FF',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '13px',
  },
  [ChipDefaultColor.RED]: {
    color: '#B42318',
    backgroundColor: '#FEF3F2',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '13px',
  },
  [ChipDefaultColor.YELLOW]: {
    color: '#BE5C23',
    backgroundColor: '#FFFAEB',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '13px',
  },
};

export const ChipDefaultColorIcon: { [key: string]: JSX.Element } = {
  [ChipDefaultColor.BLACK]: (
    <Circle sx={{ '&&': { color: '#434E59', width: '6px', height: '6px' } }} />
  ),
  [ChipDefaultColor.BLACK_BACKGROUND]: (
    <Circle sx={{ '&&': { color: '#FFFFFF', width: '6px', height: '6px' } }} />
  ),
  [ChipDefaultColor.BLUE]: (
    <Circle sx={{ '&&': { color: '#2E90FA', width: '6px', height: '6px' } }} />
  ),
  [ChipDefaultColor.GRAY]: (
    <Circle sx={{ '&&': { color: '#434E59', width: '6px', height: '6px' } }} />
  ),
  [ChipDefaultColor.GREEN]: (
    <Circle sx={{ '&&': { color: '#0FA65F', width: '6px', height: '6px' } }} />
  ),
  [ChipDefaultColor.LIGHT_BLUE]: (
    <Circle sx={{ '&&': { color: '#2E90FA', width: '6px', height: '6px' } }} />
  ),
  [ChipDefaultColor.LIGHT_GREEN]: (
    <Circle sx={{ '&&': { color: '#0FA65F', width: '6px', height: '6px' } }} />
  ),
  [ChipDefaultColor.LIGHT_RED]: (
    <Circle sx={{ '&&': { color: '#F04438', width: '6px', height: '6px' } }} />
  ),
  [ChipDefaultColor.ORANGE]: (
    <Circle sx={{ '&&': { color: '#F04438', width: '6px', height: '6px' } }} />
  ),
  [ChipDefaultColor.PURPLE]: (
    <Circle sx={{ '&&': { color: '#7A5AF8', width: '6px', height: '6px' } }} />
  ),
  [ChipDefaultColor.RED]: (
    <Circle sx={{ '&&': { color: '#F04438', width: '6px', height: '6px' } }} />
  ),
  [ChipDefaultColor.YELLOW]: (
    <Circle sx={{ '&&': { color: '#F79009', width: '6px', height: '6px' } }} />
  ),
};

export const ChipDefaultStatusStyle: { [key: string]: any } = {
  padding: '8px',
  gap: '6px',
  display: 'flex',
  height: '24px',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  borderRadius: '100px',
};

const Chip = ({
  label,
  color,
  colorStyles,
  colorIcons,
  statusStyle,
  size,
  showIcon,
  onClick,
  muiChipProps,
}: Props) => {
  const chipColor = color || ChipDefaultColor.BLACK;
  const chipColorStyle = colorStyles ? colorStyles[chipColor] : ChipDefaultColorStyle[chipColor];
  const chipStatusStyle = statusStyle || ChipDefaultStatusStyle;
  const chipSize = size || ChipSize.SMALL;

  let chipColorIcon = colorIcons ? colorIcons[chipColor] : ChipDefaultColorIcon[chipColor];
  if (!showIcon) {
    chipColorIcon = undefined;
  }

  const classes = makeStyles(() => ({
    chipColorStyle,
    chipStatusStyle,
  }))();

  return (
    <div className={classes.chipStatusStyle}>
      <MuiChip
        className={classes.chipColorStyle}
        icon={chipColorIcon}
        label={label}
        size={chipSize}
        onClick={() => {
          onClick && onClick();
        }}
        {...muiChipProps}
      />
    </div>
  );
};

export default Chip;
